import React, { useEffect, useMemo, useState } from "react";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Printer, FileText, AlertCircle } from "lucide-react";
import {
  subActivityGrade, componentAverage, categoryGradeForStudent,
  periodGradeAvgCategories, finalGradeForStudent, formatGradeWithScale,
  directPercentEquivalent,
} from "@/lib/grading";

/* eslint-disable react-hooks/exhaustive-deps */

const MONTH_OPTIONS = [
  { value: 1,  label: "January" },
  { value: 2,  label: "February" },
  { value: 3,  label: "March" },
  { value: 4,  label: "April" },
  { value: 5,  label: "May" },
  { value: 6,  label: "June" },
  { value: 7,  label: "July" },
  { value: 8,  label: "August" },
  { value: 9,  label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

export default function ReportCardPage() {
  const { selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const branchId = selectedBranch?.id;

  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState("");
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);

  // Section-scoped Report Card settings (certification toggle + recommendation + attendance month range + final grade toggle + scale-range toggle)
  const [sectionRc, setSectionRc] = useState({
    certification_enabled: false,
    certification_recommendation: "",
    attendance_start_month: 6,
    attendance_end_month: 4,
    show_final_grade: true,
    show_scale_range: true,
    show_average_column: true,
  });
  const [sectionRcSaving, setSectionRcSaving] = useState(false);

  // Load classes
  useEffect(() => {
    if (!branchId) { setClasses([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/classes", { params: { branch_id: branchId } });
        setClasses((data || []).filter((c) => c.class_type === "regular" || c.class_type === "modular"));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId]);

  // Sections
  useEffect(() => {
    setSectionId(""); setStudents([]);
    if (!classId) { setSections([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/sections", { params: { branch_id: branchId, class_id: classId } });
        setSections(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [classId, branchId]);

  // Students
  useEffect(() => {
    setStudentId("");
    if (!sectionId) { setStudents([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/students", { params: { branch_id: branchId, section_id: sectionId } });
        const list = (data || []).filter((s) => s.status !== "inactive");
        setStudents(list);
        if (list.length) setStudentId(list[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [sectionId, branchId]);

  // Load card
  useEffect(() => {
    if (!studentId || !branchId) { setCard(null); return; }
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/grading/report-card", {
          params: { branch_id: branchId, student_id: studentId },
        });
        setCard(data);
      } catch (e) { toast.error(formatApiError(e)); setCard(null); }
      finally { setLoading(false); }
    })();
  }, [studentId, branchId]);

  // Load section-scoped Report Card settings whenever the section changes.
  useEffect(() => {
    if (!sectionId || !branchId) {
      setSectionRc({
        certification_enabled: false,
        certification_recommendation: "",
        attendance_start_month: 6,
        attendance_end_month: 4,
        show_final_grade: true,
        show_scale_range: true,
        show_average_column: true,
      });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/grading/report-card/section-settings", {
          params: { branch_id: branchId, section_id: sectionId },
        });
        setSectionRc({
          certification_enabled: !!data?.certification_enabled,
          certification_recommendation: data?.certification_recommendation || "",
          attendance_start_month: Number(data?.attendance_start_month) || 6,
          attendance_end_month: Number(data?.attendance_end_month) || 4,
          show_final_grade: data?.show_final_grade !== false,
          show_scale_range: data?.show_scale_range !== false,
          show_average_column: data?.show_average_column !== false,
        });
      } catch (e) { /* silent — new sections default to disabled */ }
    })();
  }, [branchId, sectionId]);

  const saveSectionRc = async (patch) => {
    if (!sectionId || !branchId) return;
    const next = { ...sectionRc, ...patch };
    setSectionRc(next);
    setSectionRcSaving(true);
    try {
      const { data } = await api.put("/grading/report-card/section-settings", {
        branch_id: branchId, section_id: sectionId,
        certification_enabled: next.certification_enabled,
        certification_recommendation: next.certification_recommendation,
        attendance_start_month: next.attendance_start_month,
        attendance_end_month: next.attendance_end_month,
        show_final_grade: next.show_final_grade,
        show_scale_range: next.show_scale_range,
        show_average_column: next.show_average_column,
      });
      setSectionRc({
        certification_enabled: !!data?.certification_enabled,
        certification_recommendation: data?.certification_recommendation || "",
        attendance_start_month: Number(data?.attendance_start_month) || 6,
        attendance_end_month: Number(data?.attendance_end_month) || 4,
        show_final_grade: data?.show_final_grade !== false,
        show_scale_range: data?.show_scale_range !== false,
        show_average_column: data?.show_average_column !== false,
      });
      // If the month range changed, refetch the card so the attendance table
      // rebuilds with the new columns.
      if (patch.attendance_start_month !== undefined || patch.attendance_end_month !== undefined) {
        if (studentId) {
          const { data: fresh } = await api.get("/grading/report-card", {
            params: { branch_id: branchId, student_id: studentId },
          });
          setCard(fresh);
        }
      } else {
        // Sync into the loaded card so the print preview updates immediately.
        setCard((c) => c ? {
          ...c,
          section_rc_settings: {
            ...(c.section_rc_settings || {}),
            certification_enabled: !!data?.certification_enabled,
            certification_recommendation: data?.certification_recommendation || "",
            show_final_grade: data?.show_final_grade !== false,
            show_scale_range: data?.show_scale_range !== false,
            show_average_column: data?.show_average_column !== false,
          },
        } : c);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSectionRcSaving(false); }
  };

  const computed = useMemo(() => {
    if (!card) return null;
    const { categories, periods, settings, scale_ranges, tasks, scores, direct_grades } = card;
    const scoreByTask = new Map();
    for (const s of scores) scoreByTask.set(s.assessment_task_id, s.score);
    const tasksByCompByPeriod = {}; // {periodId: {componentId: tasks[]}}
    for (const t of tasks) {
      (tasksByCompByPeriod[t.grading_period_id] ||= {});
      (tasksByCompByPeriod[t.grading_period_id][t.assessment_component_id] ||= []).push(t);
    }
    const directByLevelByTargetByPeriod = {};
    for (const d of direct_grades) {
      const lvl = directByLevelByTargetByPeriod;
      (lvl[d.level] ||= {});
      (lvl[d.level][d.target_id] ||= {});
      lvl[d.level][d.target_id][d.grading_period_id] = d;
    }

    const isDirect = settings.grading_mode === "direct";
    const directLevel = settings.direct_assessment_level || "category";

    // Helper — pull the teacher-entered "Overall" direct grade for a target.
    // Returns { grade, code } where grade is the numeric equivalent (for
    // display via scale) and code is the raw letter fallback for custom
    // code-only scales that lack a percentage range.
    const overallForTarget = (level, targetId) => {
      const d = directByLevelByTargetByPeriod[level]?.[targetId]?.["__overall__"];
      if (!d) return null;
      const pct = directPercentEquivalent(
        d.direct_grade_value, d.direct_grade_code,
        settings.grading_scale_type, scale_ranges,
        settings.custom_percentage_range_enabled,
      );
      return { grade: pct, code: d.direct_grade_code || null };
    };

    const categoryRows = categories.map((cat) => {
      const perPeriod = periods.map((p) => {
        if (isDirect) {
          // Look up the direct entry depending on level
          if (directLevel === "category") {
            const d = directByLevelByTargetByPeriod["category"]?.[cat.id]?.[p.id];
            if (!d) return { period: p, grade: null, code: null, raw: null };
            const pct = directPercentEquivalent(
              d.direct_grade_value, d.direct_grade_code,
              settings.grading_scale_type, scale_ranges,
              settings.custom_percentage_range_enabled,
            );
            // Preserve the raw letter code so custom-code-only scales (no
            // percentage range) still render the saved letter instead of "—".
            return { period: p, grade: pct, code: d.direct_grade_code || null, raw: d };
          }
          if (directLevel === "component") {
            // Average direct grades across components
            const entries = cat.components
              .map((c) => directByLevelByTargetByPeriod["component"]?.[c.id]?.[p.id])
              .filter(Boolean);
            const vals = entries
              .map((d) => directPercentEquivalent(
                d.direct_grade_value, d.direct_grade_code,
                settings.grading_scale_type, scale_ranges,
                settings.custom_percentage_range_enabled,
              ))
              .filter((v) => v != null);
            if (!vals.length) {
              // Fall back to showing the first component's code so code-only
              // scales still surface something meaningful.
              const firstCode = entries.find((d) => d.direct_grade_code)?.direct_grade_code || null;
              return { period: p, grade: null, code: firstCode, raw: null };
            }
            return { period: p, grade: Math.round((vals.reduce((a, v) => a + v, 0) / vals.length) * 100) / 100, code: null, raw: null };
          }
          return { period: p, grade: null, code: null, raw: null };
        }
        // Calculated mode
        const compMap = tasksByCompByPeriod[p.id] || {};
        const grade = categoryGradeForStudent(cat.components, compMap, scoreByTask);
        return { period: p, grade, code: null, raw: null };
      });
      const periodGrades = perPeriod.map((x) => x.grade).filter((g) => g != null);
      const computedOverall = periodGrades.length
        ? Math.round((periodGrades.reduce((a, g) => a + g, 0) / periodGrades.length) * 100) / 100
        : null;
      // Prefer teacher-entered Overall (per-category or averaged across
      // components for component-level). Fall back to computed average.
      let manualOverall = null;
      if (isDirect) {
        if (directLevel === "category") {
          manualOverall = overallForTarget("category", cat.id);
        } else if (directLevel === "component") {
          const entries = cat.components
            .map((c) => overallForTarget("component", c.id))
            .filter(Boolean);
          const vals = entries.map((e) => e.grade).filter((v) => v != null);
          if (vals.length) {
            manualOverall = { grade: Math.round((vals.reduce((a, v) => a + v, 0) / vals.length) * 100) / 100, code: null };
          } else if (entries.length) {
            manualOverall = { grade: null, code: entries.find((e) => e.code)?.code || null };
          }
        }
      }
      const overall = manualOverall?.grade ?? computedOverall;
      const overallCode = manualOverall?.code || null;
      return { ...cat, perPeriod, overall, overallCode };
    });

    // Per-period average across all categories
    const periodAvgs = periods.map((p, pi) => {
      const grades = categoryRows.map((cr) => cr.perPeriod[pi]?.grade);
      const g = periodGradeAvgCategories(grades);
      return { period: p, grade: g };
    });

    // Final weighted grade
    const finalGrade = finalGradeForStudent(
      periodAvgs.map((pa) => ({ grade: pa.grade, period_percentage: pa.period.period_percentage }))
    );

    return { categoryRows, periodAvgs, finalGrade };
  }, [card]);

  const handlePrint = () => window.print();

  const catLabelSingular = card?.category_settings?.category_label === "subjects" ? "Subject"
    : card?.category_settings?.category_label === "learning_areas" ? "Learning Area"
    : card?.category_settings?.category_label === "competencies" ? "Competency"
    : (card?.category_settings?.custom_label || "Subject");

  return (
    <>
      {/* No-print filter bar */}
      <div className="no-print">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Report Cards</h1>
            <p className="text-brand-inkMuted mt-0.5 text-sm">Generate a printable summary using the saved grading setup plus the scores entered in the Grades module.</p>
          </div>
          <Button onClick={handlePrint} disabled={!card} className="rounded-2xl h-11 bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="rc-print">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
        </div>

        <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4" data-testid="rc-filters">
          <div className="grid sm:grid-cols-4 gap-3">
            <Field label="BRANCH">
              <select value={branchId || ""} onChange={(e) => setSelectedBranchId(e.target.value)} className="rc-select" data-testid="rc-branch">
                {branchesForSelectedSchool.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="CLASS / LEVEL">
              <select value={classId} onChange={(e) => setClassId(e.target.value)} className="rc-select" data-testid="rc-class">
                <option value="">— Select class —</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="SECTION">
              <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId} className="rc-select" data-testid="rc-section">
                <option value="">— Select section —</option>
                {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="STUDENT">
              <select value={studentId} onChange={(e) => setStudentId(e.target.value)} disabled={!sectionId} className="rc-select" data-testid="rc-student">
                {students.length === 0 && <option value="">— Pick a section first —</option>}
                {students.map((s) => <option key={s.id} value={s.id}>{s.full_name || `${s.name} ${s.last_name}`.trim()}</option>)}
              </select>
            </Field>
          </div>
        </section>

        {/* Section-scoped Report Card settings — visible when a section is
            picked. Contains (1) an Attendance Report month-range picker
            (defaults June → April) and (2) the Certification paragraph
            toggle + editable recommendation. All settings save per-section. */}
        {sectionId && (
          <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4 mb-4" data-testid="rc-section-settings">
            <p className="font-heading font-semibold text-foreground text-sm mb-2">Attendance Report — Month Range</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="START MONTH">
                <select
                  value={sectionRc.attendance_start_month}
                  onChange={(e) => saveSectionRc({ attendance_start_month: Number(e.target.value) })}
                  disabled={sectionRcSaving}
                  className="rc-select"
                  data-testid="rc-att-start-month"
                >
                  {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Field>
              <Field label="END MONTH">
                <select
                  value={sectionRc.attendance_end_month}
                  onChange={(e) => saveSectionRc({ attendance_end_month: Number(e.target.value) })}
                  disabled={sectionRcSaving}
                  className="rc-select"
                  data-testid="rc-att-end-month"
                >
                  {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Field>
            </div>
            <p className="text-[11px] text-brand-inkMuted mt-1">
              Defines the columns shown in the Attendance Report table. Wraps into the next calendar year if the end month is before the start month (e.g. June → April).
            </p>

            <div className="border-t border-brand-purple/10 mt-4 pt-4">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="rc-final-grade-toggle">
                <input
                  type="checkbox"
                  checked={sectionRc.show_final_grade}
                  onChange={(e) => saveSectionRc({ show_final_grade: e.target.checked })}
                  disabled={sectionRcSaving}
                  className="w-4 h-4 accent-brand-purple"
                />
                <span className="font-heading font-semibold text-foreground text-sm">
                  Show Final Grade on Report Cards & Print for this section
                </span>
              </label>
              <p className="text-[11px] text-brand-inkMuted mt-1 ml-6">
                When on, the Final Grade only averages periods that have been graded — ungraded periods are ignored so mid-year cards show an accurate running average.
              </p>
            </div>

            <div className="border-t border-brand-purple/10 mt-4 pt-4">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="rc-scale-range-toggle">
                <input
                  type="checkbox"
                  checked={sectionRc.show_scale_range}
                  onChange={(e) => saveSectionRc({ show_scale_range: e.target.checked })}
                  disabled={sectionRcSaving}
                  className="w-4 h-4 accent-brand-purple"
                />
                <span className="font-heading font-semibold text-foreground text-sm">
                  Show percentage range in the Scale legend for this section
                </span>
              </label>
              <p className="text-[11px] text-brand-inkMuted mt-1 ml-6">
                When on, the scale legend shows the range next to each code (e.g. "A · Outstanding <em>(90-95%)</em>"). Turn off to print only the codes and descriptions.
              </p>
            </div>

            <div className="border-t border-brand-purple/10 mt-4 pt-4">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="rc-avg-col-toggle">
                <input
                  type="checkbox"
                  checked={sectionRc.show_average_column}
                  onChange={(e) => saveSectionRc({ show_average_column: e.target.checked })}
                  disabled={sectionRcSaving}
                  className="w-4 h-4 accent-brand-purple"
                />
                <span className="font-heading font-semibold text-foreground text-sm">
                  Show the "Average" column on printed Report Cards for this section
                </span>
              </label>
              <p className="text-[11px] text-brand-inkMuted mt-1 ml-6">
                Turn off for mid-year cards so parents don't see a partial subject average while some periods are still ungraded. The column stays visible on-screen so teachers can still review it.
              </p>
            </div>

            <div className="border-t border-brand-purple/10 mt-4 pt-4" data-testid="rc-certification-editor">
              <div className="flex items-start gap-3">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="rc-certification-toggle">
                  <input
                    type="checkbox"
                    checked={sectionRc.certification_enabled}
                    onChange={(e) => saveSectionRc({ certification_enabled: e.target.checked })}
                    disabled={sectionRcSaving}
                    className="w-4 h-4 accent-brand-purple"
                  />
                  <span className="font-heading font-semibold text-foreground text-sm">
                    Show Certification paragraph on Report Cards for this section
                  </span>
                </label>
              </div>
              {sectionRc.certification_enabled && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-brand-inkMuted">
                    Preview:&nbsp; <em>This is to certify that <strong>[student name]</strong> has successfully completed <strong>[class]</strong> and is now recommended to <strong>[your text below]</strong>.</em>
                  </p>
                  <textarea
                    value={sectionRc.certification_recommendation}
                    onChange={(e) => setSectionRc({ ...sectionRc, certification_recommendation: e.target.value })}
                    onBlur={(e) => saveSectionRc({ certification_recommendation: e.target.value })}
                    placeholder='e.g. "Kindergarten Level"'
                    rows={2}
                    className="w-full rounded-xl border border-brand-purple/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple/40"
                    data-testid="rc-certification-recommendation"
                  />
                  <p className="text-[11px] text-brand-inkMuted">Applies to all students in this section. Saves automatically when you click away.</p>
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      {/* Card body */}
      {loading ? (
        <p className="text-brand-inkMuted p-6">Loading report card...</p>
      ) : !card ? (
        <EmptyState />
      ) : !card.periods.length || !card.categories.length ? (
        <NoSetupState />
      ) : (
        <article id="report-card" className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden print:shadow-none print:border-0 print:rounded-none" data-testid="rc-card">
          {/* Print-only top banner. If the owner uploaded a Report Card header
              photo, it spans the full paper width (replaces the logo + school
              name block). Otherwise we fall back to the classic logo + text. */}
          <div className="print-only pt-4 pb-3 border-b-2 border-foreground/80 text-center">
            {card.school?.header_photo ? (
              <img
                src={card.school.header_photo}
                alt=""
                className="w-full h-auto object-contain block"
              />
            ) : (
              <div className="flex items-center justify-center gap-3 px-6">
                {card.school?.logo && (
                  <img
                    src={card.school.logo}
                    alt=""
                    className="object-contain"
                    style={{ height: "3.25rem", width: "3.25rem" }}
                  />
                )}
                <div className="text-center">
                  <p className="font-heading text-2xl font-bold text-foreground leading-tight">
                    {card.school?.name || "School"}
                  </p>
                  {card.branch?.name && (
                    <p className="font-heading text-xl font-bold text-foreground/90 leading-tight">
                      {card.branch.name}
                    </p>
                  )}
                </div>
              </div>
            )}
            {/* When a header photo is uploaded, still print the branch name
                (bold, one size bigger) directly below it since the photo is
                typically the school-wide banner. */}
            {card.school?.header_photo && card.branch?.name && (
              <p className="font-heading text-xl font-bold text-foreground/90 leading-tight mt-2 px-6">
                {card.branch.name}
              </p>
            )}
            <p className="font-heading text-2xl font-bold text-foreground mt-3 tracking-wide text-center px-6">
              REPORT CARD
            </p>
          </div>
          {/* On-screen header (hidden on print — printed banner replaces it) */}
          <header className="rc-screen-header bg-gradient-to-br from-brand-purple to-brand-purpleHover text-white px-6 py-5 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3 print:hidden">
            <div className="flex items-center gap-4">
              {card.student?.photo
                ? <img src={card.student.photo} alt="" className="w-16 h-16 rounded-2xl object-cover border-2 border-white/40" />
                : <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center font-heading text-2xl font-bold">
                    {(card.student?.full_name || "?").charAt(0)}
                  </div>}
              <div>
                <p className="text-xs uppercase tracking-wider opacity-90 font-heading">Report Card</p>
                <h2 className="font-heading text-2xl font-bold">{card.student?.full_name}</h2>
                <p className="text-sm opacity-90">
                  {card.school?.name && <>{card.school.name} · </>}
                  {card.branch?.name}
                  {(card.student?.class_name || card.student?.section_name) ? " · " : ""}
                  {card.student?.class_name}{card.student?.section_name ? ` — ${card.student.section_name}` : ""}
                </p>
                {card.school_year?.label && <p className="text-xs opacity-80 mt-0.5">School Year {card.school_year.label}</p>}
              </div>
            </div>
            {card.section_rc_settings?.show_final_grade !== false ? (
              computed?.finalGrade != null ? (
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider opacity-90 font-heading">Final Grade</p>
                  <p className="text-3xl sm:text-4xl font-heading font-bold tabular-nums" data-testid="rc-final-grade">
                    <Grade value={computed.finalGrade} scaleType={card.settings.grading_scale_type} ranges={card.scale_ranges} />
                  </p>
                </div>
              ) : (
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider opacity-90 font-heading">Final Grade</p>
                  <p className="text-3xl sm:text-4xl font-heading font-bold tabular-nums opacity-75" data-testid="rc-final-grade">—</p>
                </div>
              )
            ) : null}
          </header>
          {/* Print-only student sub-header — student name, class + section,
              school year. No school name / branch (already in the top banner). */}
          <div className="print-only px-6 py-4 border-b border-foreground/30">
            <div className="flex items-center gap-4">
              {card.student?.photo
                ? <img src={card.student.photo} alt="" className="w-16 h-16 rounded-2xl object-cover border border-foreground/30" />
                : <div className="w-16 h-16 rounded-2xl bg-brand-purpleLight text-brand-purple flex items-center justify-center font-heading text-2xl font-bold">
                    {(card.student?.full_name || "?").charAt(0)}
                  </div>}
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground">{card.student?.full_name}</h2>
                {(card.student?.class_name || card.student?.section_name) && (
                  <p className="text-sm text-foreground/80">
                    {card.student?.class_name}
                    {card.student?.section_name ? ` — ${card.student.section_name}` : ""}
                  </p>
                )}
                {card.school_year?.label && (
                  <p className="text-sm text-foreground/70">School Year {card.school_year.label}</p>
                )}
              </div>
              {card.section_rc_settings?.show_final_grade !== false && computed?.finalGrade != null && (
                <div className="ml-auto text-right">
                  <p className="text-xs uppercase tracking-wider font-heading font-semibold text-foreground/70">Final Grade</p>
                  <p className="text-3xl font-heading font-bold tabular-nums text-foreground">
                    <Grade value={computed.finalGrade} scaleType={card.settings.grading_scale_type} ranges={card.scale_ranges} />
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Mode pill — screen-only, hidden on the printed report card */}
          <div className="px-6 py-2 bg-brand-purpleLight/30 border-b border-brand-purple/10 text-xs text-brand-inkMuted print:hidden">
            <span className="font-heading font-semibold text-foreground">Grading Mode:</span> {card.settings.grading_mode === "calculated" ? "Calculated" : `Direct Assessment (${card.settings.direct_assessment_level})`}
            <span className="mx-3">·</span>
            <span className="font-heading font-semibold text-foreground">Scale:</span> {card.settings.grading_scale_type.replace("_", " ")}
          </div>

          {/* Print-only watermark — school logo behind the full page content */}
          {card.school?.logo && (
            <img
              src={card.school.logo}
              alt=""
              aria-hidden="true"
              className="rc-watermark print-only"
            />
          )}

          {/* Grades table */}
          <div className="p-4 sm:p-6 overflow-x-auto">
            <table className="w-full text-sm" data-testid="rc-grade-table">
              <thead>
                <tr className="bg-brand-purpleLight/40 text-foreground print:bg-white print:border-b-2 print:border-foreground">
                  <th className="text-left px-3 py-2 font-heading">{catLabelSingular}</th>
                  {card.periods.map((p) => (
                    <th key={p.id} className="px-3 py-2 font-heading text-center">
                      {p.period_name}
                      {card.settings.grading_mode === "calculated" && (
                        <span className="block text-xs text-brand-inkMuted font-normal print:hidden">({p.period_percentage}%)</span>
                      )}
                    </th>
                  ))}
                  <th className={`px-3 py-2 font-heading text-center bg-brand-purpleLight/60 print:bg-white ${card.section_rc_settings?.show_average_column === false ? "print:hidden" : ""}`}>Average</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Group category rows under their group headers when
                  // grouping is enabled in the setup. Ungrouped rows fall
                  // into a synthetic "Others" bucket at the end.
                  const groupingOn = !!card.category_settings?.grouping_enabled && (card.groups || []).length > 0;
                  const rows = computed.categoryRows;
                  const colSpan = 2 + (card.periods?.length || 0);
                  if (!groupingOn) {
                    return rows.map((cr) => (
                      <tr key={cr.id} className="border-t border-brand-purple/10" data-testid={`rc-row-${cr.id}`}>
                        <td className="px-3 py-2 font-heading">{cr.category_name}</td>
                        {cr.perPeriod.map((pp, i) => (
                          <td key={pp.period_id || `pp-${cr.id}-${i}`} className="px-3 py-2 text-center tabular-nums">
                            {pp.grade != null
                              ? <Grade value={pp.grade} scaleType={card.settings.grading_scale_type} ranges={card.scale_ranges} />
                              : (pp.code || "—")}
                          </td>
                        ))}
                        <td className={`px-3 py-2 text-center tabular-nums font-heading font-semibold bg-brand-purpleLight/20 print:bg-white ${card.section_rc_settings?.show_average_column === false ? "print:hidden" : ""}`}>
                          {cr.overall != null
                            ? <Grade value={cr.overall} scaleType={card.settings.grading_scale_type} ranges={card.scale_ranges} />
                            : (cr.overallCode || "—")}
                        </td>
                      </tr>
                    ));
                  }
                  const groups = [...(card.groups || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                  const buckets = groups.map((g) => ({ group: g, rows: [] }));
                  const orphan = { group: { id: "__orphan__", group_name: "Others" }, rows: [] };
                  for (const cr of rows) {
                    const b = buckets.find((x) => x.group.id === cr.group_id);
                    (b || orphan).rows.push(cr);
                  }
                  const all = [...buckets, ...(orphan.rows.length ? [orphan] : [])].filter((b) => b.rows.length);
                  const out = [];
                  for (const b of all) {
                    out.push(
                      <tr key={`grp-${b.group.id}`} className="bg-brand-purple/10 print:bg-brand-purple/10">
                        <td colSpan={colSpan} className="px-3 py-2 font-heading font-bold text-brand-purple text-xs uppercase tracking-wider" data-testid={`rc-group-${b.group.id}`}>
                          {b.group.group_name || "Group"}
                        </td>
                      </tr>
                    );
                    for (const cr of b.rows) {
                      out.push(
                        <tr key={cr.id} className="border-t border-brand-purple/10" data-testid={`rc-row-${cr.id}`}>
                          <td className="px-3 py-2 font-heading pl-6">{cr.category_name}</td>
                          {cr.perPeriod.map((pp, i) => (
                            <td key={pp.period_id || `pp-${cr.id}-${i}`} className="px-3 py-2 text-center tabular-nums">
                              {pp.grade != null
                                ? <Grade value={pp.grade} scaleType={card.settings.grading_scale_type} ranges={card.scale_ranges} />
                                : (pp.code || "—")}
                            </td>
                          ))}
                          <td className={`px-3 py-2 text-center tabular-nums font-heading font-semibold bg-brand-purpleLight/20 print:bg-white ${card.section_rc_settings?.show_average_column === false ? "print:hidden" : ""}`}>
                            {cr.overall != null
                              ? <Grade value={cr.overall} scaleType={card.settings.grading_scale_type} ranges={card.scale_ranges} />
                              : (cr.overallCode || "—")}
                          </td>
                        </tr>
                      );
                    }
                  }
                  return out;
                })()}
                <tr className="border-t-2 border-brand-purple/30 bg-brand-purpleLight/40 print:bg-white print:border-t-2 print:border-foreground">
                  <td className="px-3 py-2 font-heading font-bold">Period Average</td>
                  {computed.periodAvgs.map((pa, i) => (
                    <td key={pa.period_id || `pa-${i}`} className="px-3 py-2 text-center tabular-nums font-heading font-bold">
                      <Grade value={pa.grade} scaleType={card.settings.grading_scale_type} ranges={card.scale_ranges} />
                    </td>
                  ))}
                  <td className={`px-3 py-2 text-center tabular-nums font-heading font-bold text-brand-purple print:text-foreground ${card.section_rc_settings?.show_average_column === false ? "print:hidden" : ""}`}>
                    <Grade value={computed.finalGrade} scaleType={card.settings.grading_scale_type} ranges={card.scale_ranges} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Scale legend */}
          {card.settings.grading_scale_type !== "percentage" && card.scale_ranges?.length > 0 && (
            <div className="px-6 pb-4">
              <p className="font-heading text-sm font-semibold text-foreground mb-2">Scale Legend</p>
              <div className="flex flex-wrap gap-2">
                {card.scale_ranges.map((r) => {
                  const showRange = card.section_rc_settings?.show_scale_range !== false;
                  const hasRange = r.min_percentage != null && r.max_percentage != null
                    && !(Number(r.min_percentage) === 0 && Number(r.max_percentage) === 0);
                  return (
                    <span key={r.code_or_value} className="text-xs px-2.5 py-1 rounded-full border border-brand-purple/20 bg-brand-purpleLight/30 print:bg-white">
                      <strong className="font-heading">{r.code_or_value}</strong>
                      {r.description ? ` · ${r.description}` : ""}
                      {showRange && hasRange ? ` (${r.min_percentage}–${r.max_percentage}%)` : ""}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Teacher comments */}
          {card.settings.allow_grading_term_comments && (
            <div className="px-6 pb-6">
              <p className="font-heading text-sm font-semibold text-foreground mb-2">Teacher Comments</p>
              <div className="space-y-2">
                {card.periods.map((p) => {
                  const c = card.comments.find((x) => x.grading_period_id === p.id);
                  return (
                    <div key={p.id} className="border border-brand-purple/15 rounded-xl p-3 print:border-foreground/20" data-testid={`rc-comment-${p.id}`}>
                      <p className="text-xs uppercase tracking-wider font-heading font-semibold text-brand-purple print:text-foreground">{p.period_name}</p>
                      <p className="text-sm text-foreground mt-1 whitespace-pre-wrap">{c?.comment || <span className="text-brand-inkMuted italic">No comment for this period.</span>}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Attendance Report — monthly grid Aug → May. Empty months show as
              blank cells so the layout matches the school's expectations. */}
          {(card.attendance_summary?.length || 0) > 0 && (
            <div className="px-6 pb-6" data-testid="rc-attendance">
              <p className="font-heading text-base font-bold text-foreground mb-2">Attendance Report</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" style={{ border: "1px solid #000" }}>
                  <thead>
                    <tr>
                      <th className="text-left px-3 py-2 font-heading" style={{ border: "1px solid #000" }}></th>
                      {card.attendance_summary.map((m) => (
                        <th key={m.label} className="px-2 py-2 font-heading text-center text-xs" style={{ border: "1px solid #000", minWidth: "48px" }}>
                          {m.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "total",   label: "Total days of school" },
                      { key: "present", label: "Total days of present" },
                      { key: "absent",  label: "Total days of absent" },
                    ].map((row) => (
                      <tr key={row.key} data-testid={`rc-att-row-${row.key}`}>
                        <td className="px-3 py-2 text-sm" style={{ border: "1px solid #000" }}>{row.label}</td>
                        {card.attendance_summary.map((m) => (
                          <td key={m.label} className="px-2 py-2 text-center tabular-nums" style={{ border: "1px solid #000" }}>
                            {m.total === 0 ? "" : m[row.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Certification paragraph — per-section toggle. Renders only when
              the teacher has enabled it for this section. The 3rd blank is
              teacher-typed; the 1st (student) + 2nd (class) auto-fill. */}
          {card.section_rc_settings?.certification_enabled && (
            <div className="px-6 pb-6 text-center" data-testid="rc-certification">
              <p className="text-base leading-relaxed text-foreground">
                This is to certify that{" "}
                <span className="font-heading font-bold underline underline-offset-4">
                  {card.student?.full_name || "________________"}
                </span>{" "}
                has successfully completed{" "}
                <span className="font-heading font-bold underline underline-offset-4">
                  {card.student?.class_name || "________________"}
                </span>{" "}
                and is now recommended to{" "}
                <span className="font-heading font-bold underline underline-offset-4">
                  {card.section_rc_settings.certification_recommendation || "________________"}
                </span>.
              </p>
            </div>
          )}

          {/* Signatories — printed at the bottom of the report card.
              Single signatory → centered. Two or more → evenly spaced across
              the page (left / right for two, spread for three+). */}
          {(card.settings.report_card_signatories?.length || 0) > 0 && (
            <div className="px-6 pt-8 pb-6" data-testid="rc-signatories">
              <div
                className={`grid gap-6 ${
                  card.settings.report_card_signatories.length === 1
                    ? "grid-cols-1 justify-items-center"
                    : card.settings.report_card_signatories.length === 2
                    ? "grid-cols-2"
                    : "grid-cols-3"
                }`}
              >
                {card.settings.report_card_signatories.map((s, i) => (
                  <div
                    key={i}
                    className={`flex flex-col items-center ${
                      card.settings.report_card_signatories.length === 2 && i === 0
                        ? "items-start sm:items-center"
                        : card.settings.report_card_signatories.length === 2 && i === 1
                        ? "items-end sm:items-center"
                        : ""
                    }`}
                    data-testid={`rc-signatory-${i}`}
                  >
                    <div className="w-56 border-b border-foreground/70 mb-1" style={{ minHeight: "2rem" }} />
                    <p className="font-heading text-sm font-bold uppercase tracking-wide text-foreground">
                      {s.name}
                    </p>
                    {s.title && (
                      <p className="text-xs text-foreground/70">{s.title}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <footer className="px-6 py-4 border-t border-brand-purple/10 text-xs text-brand-inkMuted flex flex-col sm:flex-row justify-between gap-2 print:border-t-2 print:border-foreground">
            <span>Generated by SchoolBoss on {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
            <span className="print-only">— end of report —</span>
          </footer>
        </article>
      )}

      {/* Print-only styles */}
      <style>{`
        .rc-select {
          width: 100%; height: 2.75rem; border-radius: 0.75rem;
          border: 1px solid rgba(37, 99, 235, 0.2); background: #fff;
          padding: 0 0.75rem; font-size: 0.9rem; color: hsl(var(--foreground));
        }
        .print-only { display: none; }
        @media print {
          /* Aggressive isolation: hide everything on the page, then re-show
             only the report card. This bypasses any theme sidebar / topbar /
             cookie-banner / toast that has its own display: block. */
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          body > * { display: none !important; }
          body > #root { display: block !important; }
          #root > *:not(:has(#report-card)) { display: none !important; }
          .no-print, aside, nav, header, [class*="sidebar" i],
          [class*="topbar" i], [class*="Layout__side" i], [class*="Layout__nav" i],
          [data-cookie-banner], [data-testid="topbar-user-name"],
          [data-testid="navbar-logout-btn"], [data-testid="mobile-menu-btn"],
          .Toaster, [data-sonner-toaster],
          button, .rc-select { display: none !important; }
          /* Reset any container that positions its children away from the page */
          main, section, div { position: static !important; overflow: visible !important; }
          #report-card {
            box-shadow: none !important; border: none !important;
            max-width: 100% !important; margin: 0 !important;
            position: relative !important;
          }
          #report-card, #report-card * { visibility: visible !important; }
          .print-only { display: block !important; }
          /* Full-page transparent school-logo watermark. Fixed to viewport so
             it appears on every printed page. print-color-adjust: exact keeps
             the low opacity through the print pipeline. */
          .rc-watermark {
            position: fixed !important;
            top: 50% !important; left: 50% !important;
            transform: translate(-50%, -50%) !important;
            width: 80vw !important; height: auto !important;
            max-width: 80vw !important; max-height: 80vh !important;
            object-fit: contain !important;
            opacity: 0.08 !important;
            z-index: 0 !important;
            pointer-events: none !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Ensure real content sits above the watermark */
          #report-card > *:not(.rc-watermark) { position: relative !important; z-index: 1 !important; }
          /* Let the watermark show THROUGH the grades table — cells otherwise
             paint a solid white/tinted background that clips the logo. */
          #report-card,
          #report-card article,
          #report-card table,
          #report-card thead,
          #report-card tbody,
          #report-card tr,
          #report-card th,
          #report-card td,
          #report-card section,
          #report-card footer,
          #report-card [class*="bg-"] {
            background: transparent !important;
            background-color: transparent !important;
          }
          @page { margin: 1cm; }
        }
      `}</style>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">{label}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-12 text-center no-print">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
        <FileText className="w-7 h-7 text-brand-purple" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a student</p>
      <p className="text-brand-inkMuted text-sm mt-1">Choose a class → section → student to view their report card.</p>
    </div>
  );
}

function NoSetupState() {
  return (
    <div className="bg-white rounded-2xl border-2 border-[#F59E0B]/40 bg-[#FFF7E6] p-8 text-center no-print">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FEF3C7] flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-[#B45309]" />
      </div>
      <p className="font-heading text-lg font-semibold text-[#92400E]">No grading setup found</p>
      <p className="text-[#B45309] text-sm mt-1">Configure grading periods and subjects in Grading Setup (Student menu) before generating a report card.</p>
    </div>
  );
}


/**
 * Renders a formatted grade value showing only the code / percentage — the
 * parenthesised scale description (e.g. "A (Outstanding)") is stripped both
 * on screen and on print. The full description remains visible in the Scale
 * legend at the top of the report card.
 */
function Grade({ value, scaleType, ranges }) {
  const raw = formatGradeWithScale(value, scaleType, ranges);
  const openIdx = raw.indexOf(" (");
  if (openIdx === -1 || !raw.endsWith(")")) return <>{raw}</>;
  return <>{raw.slice(0, openIdx)}</>;
}
